// @flow
import BaseModel from "../base/BaseModel";
import ContentLinkModel from "./ContentLinkModel";
import SubSectionModel from "./SubSectionModel";

import { createHashFromHref } from "../../utils/helpers/createHash";
import LinkModel from "../links/LinkModel";

import { retrieveText } from "../../utils";

import type Href from "../href/Href";
import type { ModelOptions } from "../types";

/**
 */
class SectionModel extends BaseModel {
  _entryDate: ISO_DATE | null;
  _selflink: ?LinkModel;
  _childSections: Array<SectionModel>;

  constructor(
    data: Object,
    entryDate: ISO_DATE | null,
    modelOptions?: ModelOptions,
  ) {
    super(data, {}, modelOptions);

    this._entryDate = entryDate;
  }

  /**
   */
  get referred(): boolean {
    return this.data.referred;
  }

  /**
   */
  get selflink(): LinkModel {
    if (!this._selflink) {
      this._selflink = new LinkModel(
        this.data._links?.self,
        null,
        this.modelOptions,
      );
    }
    return this._selflink;
  }

  /**
   */
  get selfhref(): Href {
    return this.selflink.href;
  }

  /**
   */
  get entryDate(): ISO_DATE | null {
    return this._entryDate;
  }

  /**
   */
  get type(): string {
    return this.data.type;
  }

  /**
   */
  get sourceLabel(): string {
    return this.data.sourceLabel;
  }

  /**
   */
  get id(): string {
    return this.data.id || this.data._id;
  }

  /**
   */
  get key(): string {
    return this.id;
  }

  /**
   * Get content label
   */
  get label(): string {
    return this.getData("label", "");
  }

  /**
   * Retrieve number of section
   */
  get number(): string | null {
    return this.data.number ?? null;
  }

  /**
   * Get content body
   */
  get body(): ?string {
    return retrieveText(this.data.body);
  }

  /**
   * Retrieve child section links
   */
  get childSectionLinks(): Array<ContentLinkModel> {
    let sections = [];
    if (this.data.childSections) {
      sections = this.data.childSections;
    } else if (this.data.sections) {
      sections = this.data.sections;
    }

    return sections.map(
      (childSection) =>
        new ContentLinkModel(
          {
            section: childSection.id || childSection._id,
            _id: childSection.id || childSection._id,
            label: childSection.label,
            sourceLabel: childSection.sourceLabel,
            _links: childSection._links,
          },
          this.entryDate,
          this.modelOptions,
        ),
    );
  }

  /**
   */
  get selfContentLink(): ContentLinkModel {
    return new ContentLinkModel(
      {
        section: this.id,
        _id: this.id,
        label: this.data.label,
        sourceLabel: this.data.sourceLabel,
        _links: this.data._links,
      },
      this.entryDate,
      this.modelOptions,
    );
  }

  /**
   * Get tree of child sections
   */
  get childSections(): Array<SectionModel> {
    if (!this._childSections) {
      let sections = [];
      if (this.data.childSections) {
        sections = this.data.childSections;
      } else if (this.data.sections) {
        sections = this.data.sections;
      }

      this._childSections = sections
        .filter((childSection) => "body" in childSection)
        .map(
          (childSection) =>
            new SectionModel(childSection, this.entryDate, this.modelOptions),
        );
    }

    return this._childSections;
  }

  /**
   * set resolved child sections
   */
  set childSections(sections: Array<SectionModel>) {
    this._childSections = sections || [];
  }

  /**
   */
  get sections(): Array<SectionModel> {
    if (this.data.sections) {
      return this.data.sections.map(
        (section) =>
          new SectionModel(section, this.entryDate, this.modelOptions),
      );
    }
    return [];
  }
  /**
   * Get sub sections
   */
  get subSections(): Array<SubSectionModel> {
    return this.data.subSections
      ? this.data.subSections.map(
          (subSection) =>
            new SubSectionModel(subSection, this.entryDate, this.modelOptions),
        )
      : [];
  }

  /**
   */
  get referenceHash(): number {
    return createHashFromHref(this.selfhref);
  }

  /**
   */
  getReferredSections(): Array<SubSectionModel | SectionModel> {
    const sections = [];

    for (const subSection of this.subSections) {
      if (subSection.referred) {
        sections.push(subSection);
      }

      const referredChilds = subSection.getReferredSections();
      sections.push(...referredChilds);
    }

    for (const childSection of this.childSections) {
      if (childSection.referred) {
        sections.push(childSection);
      }

      const referredChilds = childSection.getReferredSections();
      sections.push(...referredChilds);
    }

    return sections;
  }
}

export default SectionModel;
